import { CheckCircle, Info, Plus, Trash2, UtensilsCrossed } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '../../../ui/button';
import { Card } from '../../../ui/card';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';

interface Step9CateringProps {
  data: any;
  onChange: (updates: any) => void;
}

interface CateringPrice {
  id?: string;
  bracketId: string | null;
  serviceType: 'COFFEE_BREAK' | 'MEAL' | string;
  price: number | null;
  description: string;
}

export function Step9Catering({ data, onChange }: Step9CateringProps) {
  const cateringPrices = data.cateringPrices || [];

  const [editingServices, setEditingServices] = useState<CateringPrice[]>([]);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!initialized) {
      if (cateringPrices.length > 0) {
        setEditingServices(
          cateringPrices.map((p: any) => ({
            ...p,
            id: p.id || `temp-${Date.now()}-${Math.random()}`,
            price: null, // Force null as requested
            bracketId: null, // Force null
          }))
        );
      } else {
        const defaultServices: CateringPrice[] = [
          {
            id: `temp-${Date.now()}-1`,
            bracketId: null,
            serviceType: 'COFFEE_BREAK',
            price: null,
            description: 'Pause café',
          },
          {
            id: `temp-${Date.now()}-2`,
            bracketId: null,
            serviceType: 'MEAL',
            price: null,
            description: 'Repas',
          },
        ];
        setEditingServices(defaultServices);
        // On push initial default to parent immediately to start with standard options
        onChange({ cateringPrices: defaultServices });
      }
      setInitialized(true);
    }
  }, [cateringPrices, initialized, onChange]);

  const handleChange = (id: string, field: keyof CateringPrice, value: any) => {
    const updated = editingServices.map((p) => (p.id === id ? { ...p, [field]: value } : p));
    setEditingServices(updated);
    onChange({ cateringPrices: updated });
  };

  const handleAddService = () => {
    const newService: CateringPrice = {
      id: `temp-${Date.now()}`,
      bracketId: null,
      serviceType: 'MEAL',
      price: null,
      description: 'Nouveau service',
    };
    const updated = [...editingServices, newService];
    setEditingServices(updated);
    onChange({ cateringPrices: updated });
  };

  const handleRemoveService = (id: string) => {
    const updated = editingServices.filter((p) => p.id !== id);
    setEditingServices(updated);
    onChange({ cateringPrices: updated });
  };

  return (
    <div className="space-y-6">
      <Card className="p-6 bg-[rgba(0,117,141,0.05)] border-[rgba(0,117,141,0.2)]">
        <div className="flex items-start gap-3">
          <Info className="w-6 h-6 text-[#00758d] flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-[#00303c] mb-2">Configuration de la restauration</h4>
            <p className="text-sm text-[#005a6d]">
              La section restauration sera automatiquement affichée aux clients (sur devis) lorsqu'ils
              sélectionnent un équipement marqué comme "Déclenche la section restauration".
            </p>
            <p className="text-sm text-[#005a6d] mt-2 font-medium">
              Note : Tous les services de restauration sont fournis sur devis uniquement. Aucune tarification
              n'est requise.
            </p>
          </div>
        </div>
      </Card>

      {/* Options de restauration */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <UtensilsCrossed className="w-5 h-5 text-[#00758d]" />
            <h3 className="text-lg font-semibold">Options de restauration proposées</h3>
          </div>
          <Button type="button" onClick={handleAddService} variant="outline" size="sm">
            <Plus className="w-4 h-4 mr-2" />
            Ajouter un service
          </Button>
        </div>

        <div className="space-y-3">
          {editingServices.map((service) => (
            <Card key={service.id} className="p-4">
              <div className="grid grid-cols-[1fr_2fr_auto] gap-4 items-end">
                <div>
                  <Label>Type de service</Label>
                  <select
                    value={service.serviceType}
                    onChange={(e) => handleChange(service.id!, 'serviceType', e.target.value)}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md outline-none focus:border-[#00758d] text-sm"
                  >
                    <option value="COFFEE_BREAK">Pause café</option>
                    <option value="MEAL">Repas</option>
                    <option value="COCKTAIL">Cocktail / Apéritif</option>
                    <option value="OTHER">Autre</option>
                  </select>
                </div>

                <div className="flex-1">
                  <Label>Description précise du service</Label>
                  <Input
                    value={service.description}
                    onChange={(e) => handleChange(service.id!, 'description', e.target.value)}
                    placeholder="Ex: Pause café avec viennoiseries"
                    className="mt-1"
                  />
                </div>

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleRemoveService(service.id!)}
                  className="mb-0.5"
                >
                  <Trash2 className="w-4 h-4 text-[#8E0B56]" />
                </Button>
              </div>
            </Card>
          ))}
        </div>

        {editingServices.length === 0 && (
          <Card className="p-4 bg-gray-50 mt-4">
            <p className="text-sm text-gray-600 text-center">
              Aucun service de restauration configuré. Cliquez sur "Ajouter un service".
            </p>
          </Card>
        )}
      </div>

      {/* Résumé */}
      {editingServices.length > 0 && (
        <Card className="p-4 bg-[rgba(0,117,141,0.05)] border-[rgba(0,117,141,0.2)]">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-6 h-6 text-[#00758d] mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-[#00303c] mb-1">Résumé des services (sur devis)</h4>
              <p className="text-sm text-[#005a6d]">
                {editingServices.filter((s) => s.serviceType === 'COFFEE_BREAK').length} pause(s) café,{' '}
                {editingServices.filter((s) => s.serviceType === 'MEAL').length} repas et{' '}
                {editingServices.filter((s) => !['COFFEE_BREAK', 'MEAL'].includes(s.serviceType)).length} autre(s) service(s) proposés.
              </p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
